import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { MapPin, Truck, Navigation } from 'lucide-react';

interface RealMapProps {
  customerLocation?: { lat: number; lng: number };
  operatorLocation?: { lat: number; lng: number };
  destinationLocation?: { lat: number; lng: number };
  showRoute?: boolean;
  height?: string;
}

// Custom icons
const createCustomIcon = (iconType: 'customer' | 'operator' | 'destination') => {
  const colors = {
    customer: '#dc2626',
    operator: '#2563eb',
    destination: '#10b981'
  };

  const iconHtml = document.createElement('div');
  iconHtml.innerHTML = `
    <div style="
      width: 40px;
      height: 40px;
      background: ${colors[iconType]};
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px solid white;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      ${iconType === 'operator' ? 'animation: pulse 2s infinite;' : ''}
    ">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        ${iconType === 'customer' ? '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle>' :
          iconType === 'operator' ? '<path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-1.3h-.38L14 9z"></path><path d="M6 9H4a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h2"></path>' :
          '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>'}
      </svg>
    </div>
    <style>
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }
    </style>
  `;

  return L.divIcon({
    html: iconHtml,
    className: 'custom-map-icon',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });
};

export function RealMap({
  customerLocation,
  operatorLocation,
  destinationLocation,
  showRoute = true,
  height = '400px'
}: RealMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const routeLayerRef = useRef<L.Polyline | null>(null);
  const markersRef = useRef<L.Marker[]>([]);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Initialize map centered on Nairobi, Kenya
    const map = L.map(mapContainerRef.current).setView([-1.286389, 36.817223], 14);

    // Add OpenStreetMap tiles (free, no API key required)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing markers and route
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    if (routeLayerRef.current) {
      routeLayerRef.current.remove();
      routeLayerRef.current = null;
    }

    const bounds: L.LatLngBoundsExpression = [];

    // Add customer marker
    if (customerLocation) {
      const customerMarker = L.marker([customerLocation.lat, customerLocation.lng], {
        icon: createCustomIcon('customer')
      }).addTo(mapRef.current);
      customerMarker.bindPopup('<strong>Pickup Location</strong>');
      markersRef.current.push(customerMarker);
      bounds.push([customerLocation.lat, customerLocation.lng]);
    }

    // Add operator marker
    if (operatorLocation) {
      const operatorMarker = L.marker([operatorLocation.lat, operatorLocation.lng], {
        icon: createCustomIcon('operator')
      }).addTo(mapRef.current);
      operatorMarker.bindPopup('<strong>Operator Location</strong>');
      markersRef.current.push(operatorMarker);
      bounds.push([operatorLocation.lat, operatorLocation.lng]);
    }

    // Add destination marker
    if (destinationLocation) {
      const destMarker = L.marker([destinationLocation.lat, destinationLocation.lng], {
        icon: createCustomIcon('destination')
      }).addTo(mapRef.current);
      destMarker.bindPopup('<strong>Destination</strong>');
      markersRef.current.push(destMarker);
      bounds.push([destinationLocation.lat, destinationLocation.lng]);
    }

    // Draw route if enabled and we have both locations
    if (showRoute && operatorLocation && customerLocation) {
      const routePoints: L.LatLngExpression[] = [
        [operatorLocation.lat, operatorLocation.lng],
        [customerLocation.lat, customerLocation.lng]
      ];

      if (destinationLocation) {
        routePoints.push([destinationLocation.lat, destinationLocation.lng]);
      }

      routeLayerRef.current = L.polyline(routePoints, {
        color: '#dc2626',
        weight: 5,
        opacity: 0.8,
        dashArray: '20, 10',
        lineCap: 'round'
      }).addTo(mapRef.current);
    }

    // Fit map to show all markers with padding
    if (bounds.length > 0) {
      mapRef.current.fitBounds(bounds, { padding: [80, 80], maxZoom: 16 });
    }
  }, [customerLocation, operatorLocation, destinationLocation, showRoute]);

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: '100%',
        height,
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid var(--border)'
      }}
    />
  );
}
